import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../utils/dateUtils';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { usePageMetadata } from '../hooks/usePageMetadata';
import {
  TrendingUp,
  Users,
  ArrowRight,
  Star,
  CheckCircle,
  Shield,
  DollarSign,
  Building2,
  Award,
  Zap,
  Target,
  Sparkles,
  QrCode,
  ExternalLink,
  Apple,
  Smartphone,
  User,
  Bookmark,
  Star as StarIcon,
  Globe,
  Bell,
  PieChart,
  Home,
  Search,
  Heart,
  Settings,
  Clock,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

import FeatursCard from '../components/projects/FeatursCard';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import FAQ from '../components/common/FAQ';
import { useGetPublicProjectsQuery, useGetPublicBlogsQuery, useGetFeaturedBlogsQuery, useGetPublicTestimonialsQuery } from '../store/slices/apiSlice';

const HomePage: React.FC = () => {
  const { token } = useSelector((state: RootState) => state.auth);

  // Set page metadata
  usePageMetadata('home');
  const { data: projectsResponse, isLoading: projectsLoading } = useGetPublicProjectsQuery();
  const projects = Array.isArray(projectsResponse?.data) ? projectsResponse?.data :
    Array.isArray(projectsResponse?.data?.projects) ? projectsResponse?.data?.projects : [];

  // Fetch featured blogs from API
  const { data: featuredBlogsData, isLoading: featuredBlogsLoading } = useGetFeaturedBlogsQuery();
  const featuredBlogs = featuredBlogsData?.data || [];

  // Fetch regular blogs from API (for the smaller cards)
  const { data: blogsData, isLoading: blogsLoading } = useGetPublicBlogsQuery();
  const allBlogs = blogsData?.data?.blogs || [];

  // Fetch testimonials from API
  const { data: testimonialsData, isLoading: testimonialsLoading, error: testimonialsError } = useGetPublicTestimonialsQuery();
  const allTestimonials = testimonialsData?.data?.testimonials || [];

  // Transform featured blog data for homepage
  const transformedFeaturedBlogs = Array.isArray(featuredBlogs) ? featuredBlogs.map((blog: any) => ({
    id: blog.id,
    slug: blog.slug,
    title: blog.title,
    excerpt: blog.desc,
    author: {
      name: blog.author,
      role: "Author",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&h=150&q=80"
    },
    category: blog.category,
    readTime: `${blog.readTime} min read`,
    publishDate: blog.publishedAt,
    image: blog.image || "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400&q=80",
    views: blog.views,
    likes: blog.likes,
    comments: blog.comments,
    featured: blog.featured
  })) : [];

  // Transform all blog data for homepage (use all blogs, not just featured)
  const transformedAllBlogs = Array.isArray(allBlogs) ? allBlogs
    .map((blog: any) => ({
      id: blog.id,
      slug: blog.slug,
      title: blog.title,
      excerpt: blog.desc,
      author: {
        name: blog.author,
        role: "Author",
        image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&h=150&q=80"
      },
      category: blog.category,
      readTime: `${blog.readTime} min read`,
      publishDate: blog.publishedAt,
      image: blog.image || "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400&q=80",
      views: blog.views,
      likes: blog.likes,
      comments: blog.comments,
      featured: blog.featured
    })) : [];

  // Get the 3 most recent blogs for homepage display
  const homepageBlogs = transformedAllBlogs.slice(0, 3);

  // Transform testimonials data for homepage
  const transformedTestimonials = allTestimonials
    .filter((testimonial: any) => testimonial.status === 'approved')
    .slice(0, 3) // Get only first 3 testimonials for homepage
    .map((testimonial: any) => ({
      id: testimonial.id,
      name: testimonial.name,
      role: testimonial.profile || 'Investor',
      content: testimonial.text,
      rating: testimonial.rating,
      location: testimonial.location,
      verified: testimonial.verified,
      category: testimonial.category,
      helpful: testimonial.helpful,
      created_at: testimonial.created_at
    }));

  // Get the 3 blogs for homepage display
  const displayBlogs = homepageBlogs;

  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 3);
    }, 5000);
    return () => clearInterval(interval);
  }, []);



  // Premium Features
  const premiumFeatures = [
    {
      icon: Shield,
      title: "Bank-Grade Security",
      description: "256-bit encryption with biometric authentication for maximum security",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: Zap,
      title: "Real-Time Updates",
      description: "Instant notifications and live market data for informed decisions",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: Target,
      title: "Smart Investment",
      description: "AI-powered recommendations and auto-investment strategies",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: Globe,
      title: "Global Access",
      description: "Invest from anywhere with 24/7 platform access",
      color: "from-blue-500 to-blue-600"
    }
  ];


  // Hero Carousel Data
  const heroSlides = [
    {
      title: "Premium Investment Opportunities",
      subtitle: "Join thousands of investors building wealth through carefully curated opportunities",
      image: "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=800&q=80",
      cta: "Explore Projects"
    },
    {
      title: "Smart Investment Platform",
      subtitle: "AI-powered recommendations and real-time market insights for optimal returns",
      image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=800&q=80",
      cta: "Get Started"
    },
    {
      title: "Secure & Transparent",
      subtitle: "Bank-grade security with complete transparency in all transactions",
      image: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&h=800&q=80",
      cta: "Learn More"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <Navbar />

      {/* Hero Section - Premium Carousel */}
      <section className="relative h-screen overflow-hidden">
        {/* Carousel */}
        <div className="relative h-full">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${currentSlide === index ? 'opacity-100' : 'opacity-0'
                }`}
            >
              {/* Background   Image */}
              <div className="absolute inset-0">
                <img
                  src={slide.image}
                  alt={slide.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
              </div>

              {/* Content */}
              <div className="relative z-10 h-full flex items-center">
                <div className="container mx-auto px-6">
                  <div className="max-w-4xl">
                    <div className={`transition-all duration-1000 delay-300 ${currentSlide === index ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                      }`}>
                      <div className="inline-flex items-center gap-2 bg-blue-600 px-4 py-2 rounded-full text-sm font-semibold text-white mb-6">
                        <Sparkles className="w-4 h-4" />
                        Premium Investment Platform
                      </div>

                      <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                        {slide.title}
                      </h1>

                      <p className="text-xl text-gray-300 mb-8 max-w-2xl leading-relaxed">
                        {slide.subtitle}
                      </p>

                      <div className="flex flex-col sm:flex-row gap-4 mb-8">
                        <Link
                          to="/projects"
                          className="group bg-white text-black px-8 py-4 rounded-2xl font-semibold shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 flex items-center justify-center gap-4"
                        >
                          {slide.cta}
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                        </Link>

                        {!token ? (
                          <a
                            href="https://app.diasporawise.com"
                            className="group border-2 border-white text-white px-8 py-4 rounded-2xl font-semibold hover:bg-white hover:text-black transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 flex items-center justify-center gap-4 lg:hidden"
                          >
                            Get Started
                            <User className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                          </a>
                        ) : (
                          <Link
                            to="/dashboard"
                            className="group bg-white text-black px-8 py-4 rounded-2xl font-semibold shadow-2xl hover:shadow-green-500/25 transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 flex items-center justify-center gap-4"
                          >
                            Go to Dashboard
                            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                          </Link>
                        )}
                      </div>


                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Carousel Controls */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-4">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${currentSlide === index ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/75'
                }`}
            />
          ))}
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-pulse"></div>
          </div>
        </div>
      </section>

      {/* Premium Features Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-600 px-4 py-2 rounded-full text-sm font-semibold text-white mb-6">
              <Sparkles className="w-4 h-4" />
              Why Choose Diasporawise?
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Premium Investment Experience
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience the most advanced investment platform with cutting-edge features and security.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {premiumFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={index}
                  className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-4 cursor-pointer border border-gray-100"
                  style={{ animationDelay: `${index * 150}ms` }}
                >
                  <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 sm:mb-12 gap-6">
            <div className="w-full lg:w-auto">
              <div className="inline-flex items-center gap-2 bg-blue-600 px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-semibold text-white mb-3 sm:mb-4">
                <Star className="w-3 h-3 sm:w-4 sm:h-4" />
                Featured Projects
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3 sm:mb-4 leading-tight">
                Premium Investment Opportunities
              </h2>
              <p className="text-sm sm:text-base md:text-lg text-gray-600 max-w-2xl leading-relaxed">
                Discover carefully curated investment projects with exceptional returns and proven track records.
              </p>
            </div>
            <Link
              to="/projects"
              className="group bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2 w-full sm:w-auto justify-center lg:justify-start"
            >
              View All Projects
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:gap-6 md:gap-8">
            {projectsLoading ? (
              <div className="col-span-full flex justify-center items-center py-12 sm:py-16 md:py-20">
                <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : projects.length === 0 ? (
              <div className="col-span-full text-center py-12 sm:py-16 md:py-20">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                  <Building2 className="w-8 h-8 sm:w-10 sm:h-10 text-blue-600" />
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 sm:mb-4">No Projects Available</h3>
                <p className="text-sm sm:text-base text-gray-600 max-w-md mx-auto">Check back soon for new investment opportunities.</p>
              </div>
            ) : projects.slice(0, 1).map((project: any) => {
              const progress = project.funding_goal && project.amount_raised ? Math.round((project.amount_raised / project.funding_goal) * 100) : 0;
              return (
                <FeatursCard
                  key={project.project_id}
                  property={{
                    id: project.project_id,
                    title: project.title,
                    address: project.location,
                    image: project.images && project.images.length > 0 ? project.images[0].image_url : '',
                    countdown: project.deadline ? formatDateUtil(project.deadline) : '',
                    progress,
                    investors: project.investors || 0,
                    raised: project.amount_raised || 0,
                    goal: project.funding_goal || 0,
                    annualReturn: project.expected_roi_percentage ? `${project.expected_roi_percentage}%` : '',
                    maxTerm: project.roi_duration_months ? `${project.roi_duration_months} months` : '',
                    propertyType: project.category?.name || '',
                    distribution: project.distribution || 'N/A',
                  }}
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* Real Estate Photos Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-600 px-4 py-2 rounded-full text-sm font-semibold text-white mb-6">
              <Building2 className="w-4 h-4" />
              Real Estate Portfolio
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Premium Nigerian Properties
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore our curated collection of verified real estate projects across Nigeria's most promising locations.
            </p>
          </div>

          {/* Desktop Grid - 3 columns */}
          <div className="hidden md:grid grid-cols-3 gap-8">
            {[
              {
                image: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
                title: "Victoria Island Luxury Apartments",
                location: "Lagos",
                type: "Residential"
              },
              {
                image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
                title: "Abuja Business District",
                location: "Abuja",
                type: "Commercial"
              },
              {
                image: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
                title: "Port Harcourt Estate",
                location: "Port Harcourt",
                type: "Fractional"
              }
            ].map((property, index) => (
              <div key={index} className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-4 overflow-hidden border border-gray-100">
                <div className="relative overflow-hidden">
                  <img
                    src={property.image}
                    alt={property.title}
                    className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{property.title}</h3>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-gray-500" />
                          <span className="text-sm text-gray-600">{property.location}</span>
                        </div>
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                          {property.type}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile Carousel */}
          <div className="md:hidden">
            <div className="relative overflow-hidden">
              <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
                {[
                  {
                    image: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
                    title: "Victoria Island Luxury Apartments",
                    location: "Lagos",
                    type: "Residential"
                  },
                  {
                    image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
                    title: "Abuja Business District",
                    location: "Abuja",
                    type: "Commercial"
                  },
                  {
                    image: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=600&q=80",
                    title: "Port Harcourt Estate",
                    location: "Port Harcourt",
                    type: "Fractional"
                  }
                ].map((property, index) => (
                  <div key={index} className="w-full flex-shrink-0 px-4">
                    <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
                      <div className="relative overflow-hidden">
                        <img
                          src={property.image}
                          alt={property.title}
                          className="w-full h-64 object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        <div className="absolute bottom-4 left-4 right-4">
                          <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4">
                            <h3 className="text-lg font-bold text-gray-900 mb-2">{property.title}</h3>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Globe className="w-4 h-4 text-gray-500" />
                                <span className="text-sm text-gray-600">{property.location}</span>
                              </div>
                              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                                {property.type}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile Carousel Controls */}
            <div className="flex justify-center mt-6 gap-2">
              {[0, 1, 2].map((index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${currentSlide === index ? 'bg-blue-600 scale-125' : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Showcase Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-900 via-blue-900 to-black text-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="inline-flex items-center gap-2 bg-blue-600 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Smartphone className="w-4 h-4" />
                Mobile Investment App
              </div>

              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                Invest on the Go with
                <span className="block text-blue-300">
                  Diasporawise Mobile
                </span>
              </h2>

              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Access your investments anytime, anywhere with our premium mobile application.
                Real-time updates, secure transactions, and intuitive interface.
              </p>

              {/* App Features */}
              <div className="space-y-4 mb-8">
                {[
                  "Real-time portfolio tracking",
                  "Secure biometric authentication",
                  "Instant investment notifications",
                  "AI-powered recommendations"
                ].map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span className="text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Download Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <a
                  href="https://play.google.com/store/apps/details?id=com.realestateinvest.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-white text-black px-8 py-4 rounded-2xl font-semibold shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 flex items-center justify-center gap-4"
                >
                  <div className="w-8 h-8 bg-black rounded-lg flex items-center justify-center">
                    <div className="w-4 h-4 bg-white rounded-sm"></div>
                  </div>
                  <div className="text-left">
                    <div className="text-xs font-medium">GET IT ON</div>
                    <div className="text-lg font-bold">Google Play</div>
                  </div>
                  <ExternalLink className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </a>

                <a
                  href="#"
                  className="group bg-white text-black px-8 py-4 rounded-2xl font-semibold shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 flex items-center justify-center gap-4"
                >
                  <Apple className="w-8 h-8" />
                  <div className="text-left w-full sm:w-auto">
                    <div className="text-[10px] sm:text-xs font-medium">Download on the</div>
                    <div className="text-base sm:text-lg font-bold">App Store</div>
                  </div>
                  <ExternalLink className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300 hidden sm:block" />
                </a>
              </div>

              {/* QR Code */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 inline-block w-full sm:w-auto">
                <h3 className="text-base sm:text-lg font-semibold text-white mb-4 flex items-center gap-2 justify-center sm:justify-start">
                  <QrCode className="w-5 h-5" />
                  Scan QR Code to Download
                </h3>
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <div className="w-48 h-48 sm:w-56 sm:h-56 bg-white rounded-xl flex items-center justify-center shadow-lg overflow-hidden p-2">
                    <img
                      src="/diasporwise qr play store.jpg"
                      alt="QR Code for Download"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="text-center sm:text-left text-white/90">
                    <p className="font-medium mb-1 text-sm sm:text-base">Point your camera at the QR code</p>
                    <p className="text-xs sm:text-sm text-gray-400">Available for Android & iOS</p>
                    <div className="flex gap-2 mt-4 sm:mt-2 justify-center sm:justify-start">
                      <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Phone Mockup */}
            <div className="relative flex justify-center lg:justify-end">
              <div className="relative">
                <img
                  src="/app_image-removebg-preview.png"
                  alt="Diasporawise Mobile App"
                  className="w-full max-w-[400px] h-auto rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Blog Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 bg-blue-600 px-6 py-3 rounded-full text-sm font-bold text-white mb-8 shadow-lg">
              <Bookmark className="w-5 h-5" />
              Latest Insights
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-8 leading-tight">
              Investment Insights & News
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Stay updated with the latest trends, market analysis, and investment strategies from our expert team.
            </p>
          </div>

          {featuredBlogsLoading || blogsLoading ? (
            <div className="flex justify-center items-center py-24">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600"></div>
            </div>
          ) : displayBlogs.length === 0 ? (
            <div className="text-center py-24">
              <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-8">
                <Bookmark className="w-12 h-12 text-blue-600" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-4">No Blog Posts Yet</h3>
              <p className="text-lg text-gray-600 mb-8">Check back soon for the latest investment insights and news.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {displayBlogs.map((blog) => (
                <div key={blog.id} className="group cursor-pointer">
                  <Link to={`/blog/${blog.slug || blog.id}`}>
                    <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden border border-gray-100 h-full">
                      <div className="relative overflow-hidden">
                        <img
                          src={blog.image}
                          alt={blog.title}
                          className="w-full h-56 object-contain bg-gray-100 group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
                        {blog.featured && (
                          <div className="absolute top-4 left-4 bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                            Featured
                          </div>
                        )}
                        <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm text-gray-700 px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 shadow-lg">
                          <Clock className="w-3 h-3" />
                          {blog.readTime}
                        </div>
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${blog.featured
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-blue-100 text-blue-700'
                            }`}>
                            {blog.category}
                          </span>
                          <span className="text-gray-500 text-xs font-medium">• {formatDateUtil(blog.publishDate)}</span>
                        </div>
                        <h4 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300 leading-tight">
                          {blog.title}
                        </h4>
                        <p className="text-gray-600 text-sm mb-6 leading-relaxed line-clamp-3">
                          {blog.excerpt}
                        </p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center text-white font-bold ${blog.featured
                              ? 'bg-blue-600'
                              : 'bg-blue-600'
                              }`}>
                              {blog.author.name.charAt(0).toUpperCase()}
                            </div>
                            <span className="text-sm font-bold text-gray-900">{blog.author.name}</span>
                          </div>
                          <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all duration-300" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          )}

          {/* View All Blogs Button */}
          <div className="text-center">
            <Link
              to="/blogs"
              className="group inline-flex items-center gap-4 bg-blue-600 text-white px-10 py-5 rounded-2xl font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 hover:scale-105"
            >
              View All Blog Posts
              <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-20 px-6 bg-blue-600">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-semibold text-white mb-6">
            <Sparkles className="w-4 h-4" />
            Ready to Start Investing?
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Join Diasporawise Today
          </h2>

          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Start your investment journey with the most trusted investment platform.
            Join thousands of investors building wealth through premium opportunities.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link
              to="/projects"
              className="group bg-white text-black px-8 py-4 rounded-2xl font-semibold shadow-2xl hover:shadow-purple-500/25 transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 flex items-center justify-center gap-4"
            >
              Explore Projects
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>

            {!token ? (
              <a
                href="https://app.diasporawise.com"
                className="group border-2 border-white text-white px-8 py-4 rounded-2xl font-semibold hover:bg-white hover:text-black transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 flex items-center justify-center gap-4 lg:hidden"
              >
                Create Account
                <User className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
              </a>
            ) : (
              <Link
                to="/admin/profile"
                className="group bg-white text-black px-8 py-4 rounded-2xl font-semibold shadow-2xl hover:shadow-green-500/25 transition-all duration-300 transform hover:-translate-y-2 hover:scale-105 flex items-center justify-center gap-4"
              >
                Go to Profile
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            )}
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 inline-block">
            <h3 className="text-lg font-semibold text-white mb-4">Download Mobile App</h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#"
                className="group bg-white text-black px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center justify-center gap-3"
              >
                <div className="w-6 h-6 bg-black rounded flex items-center justify-center">
                  <div className="w-3 h-3 bg-white rounded-sm"></div>
                </div>
                <div className="text-left">
                  <div className="text-xs font-medium">GET IT ON</div>
                  <div className="text-sm font-bold">Google Play</div>
                </div>
              </a>

              <a
                href="#"
                className="group bg-white text-black px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center justify-center gap-3"
              >
                <Apple className="w-6 h-6" />
                <div className="text-left">
                  <div className="text-xs font-medium">Download on the</div>
                  <div className="text-sm font-bold">App Store</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQ
        title="Investment Questions & Answers"
        subtitle="Everything you need to know about investing with Diasporawise"
        faqs={[
          {
            id: 1,
            question: "How do I get started with investing on Diasporawise?",
            answer: "Getting started is easy! Simply create an account, complete the verification process, browse our curated investment opportunities, and start investing with as little as $100. Our platform guides you through each step with detailed project information and risk assessments."
          },
          {
            id: 2,
            question: "What is the minimum investment amount?",
            answer: "The minimum investment amount varies by project, but most opportunities start from $100. This low barrier to entry makes premium investments accessible to a wide range of investors, from beginners to experienced professionals."
          },
          {
            id: 3,
            question: "How secure is my investment and personal data?",
            answer: "Security is our top priority. We use bank-grade encryption, secure payment processing, and comply with international financial regulations. Your investments are protected by comprehensive insurance and our platform undergoes regular security audits."
          },
          {
            id: 4,
            question: "What types of investment opportunities are available?",
            answer: "We offer a diverse range of investment opportunities including real estate projects, technology startups, infrastructure developments, and sustainable energy projects. Each opportunity is carefully vetted by our expert team for potential returns and risk assessment."
          },
          {
            id: 5,
            question: "How do I track my investment performance?",
            answer: "Our dashboard provides real-time tracking of all your investments, including current values, returns, and detailed performance analytics. You'll receive regular updates and can access comprehensive reports anytime through our platform or mobile app."
          },
          {
            id: 6,
            question: "What fees does Diasporawise charge?",
            answer: "We believe in transparent pricing. Our fees are clearly disclosed upfront and typically range from 1-3% depending on the investment type. There are no hidden fees, and you'll see exactly what you're paying before making any investment decisions."
          }
        ]}
      />

      {/* Testimonials Section - Just Above Footer */}
      <section className="py-16 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-600 px-4 py-2 rounded-full text-sm font-semibold text-white mb-6 shadow-md">
              <Star className="w-4 h-4" />
              What Our Investors Say
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 leading-tight">
              Investing Made Easy for the Diaspora
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
              Secure, Profitable, and Transparent Opportunities
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            {/* Main Quote Section */}
            <div className="bg-white rounded-2xl p-8 shadow-lg mb-12 border border-gray-100">
              <blockquote className="text-lg text-gray-700 leading-relaxed mb-6 italic text-center">
                "Around the globe, millions of people live far away from their home countries yet still maintain enduring ties to their roots. This global population, also referred to as the diaspora, is a major force behind the creation of economies through diaspora investments."
              </blockquote>

              <div className="bg-blue-600 rounded-xl p-6 mb-6 shadow-md">
                <p className="text-white font-medium leading-relaxed text-center">
                  <strong>Pull Quote:</strong> "In the past, sending money home used to be confined to remittances. But now, with platforms like Diasporawise, investments have been redefined. Today, rather than sending one-off remittances, the diaspora can participate in real estate investment in Nigeria, commercial property investment in Nigeria, residential property investment in Nigeria, and even fractional property investment with a few taps of their screen."
                </p>
              </div>

              <p className="text-gray-700 leading-relaxed text-center">
                This overlap is helping to unlock safe, transparent, lucrative investment options for people who wish to build wealth while doing good for their own country's economic development.
              </p>
            </div>

            {/* Testimonial Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              {testimonialsLoading ? (
                <div className="col-span-full flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                </div>
              ) : testimonialsError ? (
                <div className="col-span-full text-center py-12">
                  <div className="text-gray-400 text-4xl mb-3">💬</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Unable to Load Testimonials</h3>
                  <p className="text-gray-600">There was an error loading customer testimonials.</p>
                </div>
              ) : transformedTestimonials.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <div className="text-gray-400 text-4xl mb-3">💬</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">No Testimonials Yet</h3>
                  <p className="text-gray-600">Be the first to share your experience with us!</p>
                </div>
              ) : (
                transformedTestimonials.slice(0, 3).map((testimonial, index) => {
                  // Clean and format testimonial content - remove all URLs and image paths
                  const cleanContent = testimonial.content
                    ? testimonial.content
                      .replace(/https?:\/\/[^\s]+/g, '') // Remove HTTP/HTTPS URLs
                      .replace(/www\.[^\s]+/g, '') // Remove www URLs
                      .replace(/[^\w\s.,!?\-']/g, ' ') // Remove special characters except basic punctuation
                      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
                      .trim()
                    : '';

                  // Ensure content is meaningful and not just URLs
                  const displayContent = cleanContent.length > 15 && !cleanContent.match(/^(uploads|media|images|photos)/i)
                    ? cleanContent
                    : 'Excellent service and great investment opportunities with Diasporawise!';

                  // Format name properly
                  const formattedName = testimonial.name
                    ? testimonial.name.split(' ').map((word: string) =>
                      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                    ).join(' ')
                    : 'Anonymous User';

                  // Clean role - remove URLs and format properly
                  const cleanRole = testimonial.role && testimonial.role !== 'Investor'
                    ? testimonial.role.replace(/https?:\/\/[^\s]+/g, '').trim() || 'Verified Investor'
                    : 'Verified Investor';

                  return (
                    <div
                      key={testimonial.id}
                      className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden border border-gray-100 group"
                      style={{ animationDelay: `${index * 150}ms` }}
                    >
                      {/* Rating and Verification */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-1">
                          {[...Array(testimonial.rating || 5)].map((_, i) => (
                            <StarIcon key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                          ))}
                        </div>
                        {testimonial.verified && (
                          <div className="flex items-center text-gray-600 bg-gray-100 px-2 py-1 rounded-full border border-gray-200">
                            <Shield className="w-3 h-3 mr-1" />
                            <span className="text-xs font-medium">Verified</span>
                          </div>
                        )}
                      </div>

                      {/* Testimonial Content */}
                      <blockquote className="text-gray-700 mb-6 leading-relaxed italic text-sm">
                        "{displayContent}"
                      </blockquote>

                      {/* User Information */}
                      <div className="flex items-center gap-4">
                        <div className="h-12 w-12 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0 shadow-md group-hover:scale-105 transition-transform duration-300">
                          {formattedName.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 mb-1 text-sm">{formattedName}</h4>
                          <p className="text-gray-600 text-xs mb-2">{cleanRole}</p>
                          {testimonial.location && (
                            <div className="flex items-center gap-1 mb-2">
                              <Globe className="w-3 h-3 text-gray-400 flex-shrink-0" />
                              <span className="text-gray-500 text-xs">{testimonial.location}</span>
                            </div>
                          )}
                          {testimonial.category && (
                            <div className="mt-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                {testimonial.category}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* View All Testimonials Button */}
            <div className="text-center">
              <Link
                to="/testimonials"
                className="group inline-flex items-center gap-3 bg-gray-800 text-white px-8 py-4 rounded-xl font-semibold shadow-sm hover:shadow-md transition-all duration-200"
              >
                View All Testimonials
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Learn More About Diasporawise Section */}
      <section className="bg-white py-12 px-4 border-t border-gray-200">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <button
              onClick={() => setIsLearnMoreOpen(!isLearnMoreOpen)}
              className="flex items-center justify-center gap-2 mx-auto bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors duration-300 border border-gray-300"
              aria-expanded={isLearnMoreOpen}
              aria-controls="diasporawise-content"
            >
              <span className="font-medium">
                {isLearnMoreOpen ? 'Hide Learn More About Diasporawise' : 'Learn More About Diasporawise'}
              </span>
              {isLearnMoreOpen ? (
                <ChevronUp className="w-4 h-4 text-gray-600" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-600" />
              )}
            </button>
          </div>

          <div
            id="diasporawise-content"
            className={`overflow-hidden transition-all duration-500 ease-in-out ${isLearnMoreOpen ? 'max-h-none opacity-100' : 'max-h-0 opacity-0'
              }`}
          >
            <article className="prose prose-lg max-w-none">
              {/* Main Content Section */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Investing Made Easy for the Diaspora: Secure, Profitable, and Transparent Opportunities
                </h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Around the globe, millions of people live far away from their home countries yet still maintain enduring ties to their roots. This global population, also referred to as the diaspora, is a major force behind the creation of economies through diaspora investments. For those in the Nigerian diaspora, like many other African communities abroad, an opportunity to send funds back home represents more than white-collar wealth transfer; it's about impact, security, and legacy.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  <strong>Pull Quote:</strong> "In the past, sending money home used to be confined to remittances. But now, with platforms like Diasporawise, investments have been redefined. Today, rather than sending one-off remittances, the diaspora can participate in real estate investment in Nigeria, commercial property investment in Nigeria, residential property investment in Nigeria, and even fractional property investment with a few taps of their screen."
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  This overlap is helping to unlock safe, transparent, lucrative investment options for people who wish to build wealth while doing good for their own country's economic development.
                </p>
              </div>

              {/* Section 1 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">1. Who is Diasporawise and Why Do We Exist?</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Diasporawise is more than just an investment platform. We are the trusted bridge between diaspora investors and vetted projects in Nigeria.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">Our mission is simple:</p>
                <ul className="list-disc list-inside text-gray-700 mb-6 space-y-2">
                  <li>Keep investing outside your home country stress-free.</li>
                  <li>Make sure every venture is verified, transparent, and profitable.</li>
                  <li>Help investors around the world access high-yield investing opportunities without the high risk.</li>
                </ul>
              </div>

              {/* Section 2 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">2. What Makes Diasporawise Different?</h2>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Transparency</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  All projects are thoroughly documented with transparent ROI estimations.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Access</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Whether you're looking to invest in property on a fractional basis or via larger projects, we cater for all budgets.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Impact</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Your money helps create homes, businesses, and communities in Nigeria.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Safety</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Rigorous due diligence ensures your money is safe and growth is predictable.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Whether you're investing as part of the Nigerian diaspora in the UK, US, Europe, or Asia — investing with Diasporawise has never been easier.
                </p>
              </div>

              {/* Section 3 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">3. How Diasporawise Works: Fast & Hassle-Free</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  We believe investing should be simple. With Diasporawise, the journey is divided into four easy steps:
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Sign Up</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Register for your free investor account from anywhere in the world.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Explore Projects</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Choose from residential, commercial, and real estate investment in Nigeria.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Invest Securely</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Full property investment or fractional investment (depending on your budget).
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Track Returns</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Watch your portfolio grow and earn passive income.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  No intermediaries. No hidden fees. Just transparent investment opportunities designed for the diaspora.
                </p>
              </div>

              {/* Section 4 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">4. Sources of Diaspora Investments: Creating Local Impact</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Diasporawise connects diaspora communities directly to Nigeria's real estate market.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">UK Based Investors</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Leverage strong currency to invest in Nigerian real estate.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">USA Investors</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Access high-return residential and commercial projects.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">European Investors</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Diversify with long-term real estate investments without being tied to one location.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Asian Investors</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Discover affordable entry points into Nigeria's growing property market.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Each option is optimized for financial growth while keeping investors connected to their roots.
                </p>
              </div>

              {/* Section 5 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">5. Investment Options with Diasporawise</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  We provide a range of profitable investment opportunities for diaspora investors. Some of the most popular include:
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Nigeria Real Estate Investment</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Access high-demand projects with long-term appreciation.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Investing in Real Estate from Abroad</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  From London, New York, Paris, or Dubai, you can invest in verified Nigerian properties with full transparency.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Commercial Property Investment Nigeria</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Office spaces, malls, and co-working hubs offer lucrative rental income.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Residential Property Investment Nigeria</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Generate steady rental income from Nigeria's growing housing demand.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Fractional Property Investment Nigeria</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Begin with smaller amounts and still own a share of premium properties.
                </p>
              </div>

              {/* Section 6 */}
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">6. Why Choose Diasporawise?</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  With many platforms offering returns, here's why we stand out:
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Security</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Rigorous due diligence on every investment.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Transparency</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  No hidden charges, clear terms, and honest communication.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Accessibility</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Opportunities for all budgets, from fractional stakes to full projects.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Impact</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Your investments help build homes, businesses, and infrastructure in Nigeria.
                </p>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">Global Reach</h3>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Tailored solutions for investors in the UK, US, Europe, and Asia.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  At Diasporawise, our focus is to make diaspora investment easy, lucrative, and stress-free.
                </p>
              </div>

              {/* Section 7 - Conclusion */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">7. Conclusion: Create Wealth, Build Legacy</h2>
                <p className="text-gray-700 leading-relaxed mb-6">
                  The days of traditional remittances are behind us. With Diasporawise, diaspora investors can now access secure, profitable, and transparent opportunities to grow wealth while staying connected to Nigeria.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  From purchasing real estate to fractional and commercial property investments, we provide trusted channels for every investor to make an impact and build a lasting legacy.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default HomePage;